import type { Cursus } from '../lib/types'

/* ===========================================================================
 *  Inkoopfacturen: van mail tot betaald
 *
 *  LEES DIT VOORDAT JE IETS WIJZIGT
 *  ---------------------------------
 *
 *  Deze cursus beschrijft een systeem dat bestaat. Elke stap hieronder is
 *  nagekeken in de dashboard-repo en staat daar in de migraties:
 *
 *    0044  het geheugen: hoe deze leverancier vorige keer is geboekt
 *    0049  de pc thuis leest de bon, de server verwerkt hem
 *    0050  drie keer hetzelfde mag de vierde keer vanzelf
 *    0058  wat een boeking bij Exact nodig heeft
 *    0060  vier ogen: eerst nakijken, dan tekenen
 *    0062  een factuur splitsen
 *    0065  betalen en SEPA
 *    0079  de bon weet zelf in welke bv hij hoort
 *    0091  een boeking die niet optelt
 *    0110  meerdere mensen per stap
 *    0113  een vastgelopen bon opnieuw aanbieden
 *
 *  Dat is belangrijker dan bij de AI-cursus: dit gaat over geld dat weggaat.
 *  Een cursus die iets anders beweert dan het scherm doet, is erger dan geen
 *  cursus -- dan leert iemand een handeling aan die niet bestaat en denkt hij
 *  dat hij iets heeft afgevinkt.
 *
 *  Verandert er iets aan de stroom, dan hoort dit bestand mee te veranderen
 *  en gaat `versie` omhoog. Dan moet iedereen hem opnieuw doen, en dat is hier
 *  de bedoeling: wie de oude route in zijn hoofd heeft, doet straks de
 *  verkeerde handeling.
 *
 *  Namen van schermen
 *  ------------------
 *
 *  De schermnamen hieronder zijn letterlijk die van het dashboard
 *  (src/dashboards/administratie/ en src/lib/schermen.ts): Postvak,
 *  Inkoopfacturen, Leveranciers, Te verwerken, Betalen, De bv's. Verandert
 *  daar een naam, dan hier ook -- een cursus die naar een knop verwijst die
 *  anders heet, kost meer tijd dan hij bespaart.
 * =========================================================================== */

export const INKOOPFACTUREN: Cursus = {
  id: 'inkoopfacturen',
  versie: 2,
  titel: 'Inkoopfacturen: van mail tot betaald',
  ondertitel: 'Wat de computer doet, wat jij doet, en waar je naar kijkt',
  belofte:
    'Na deze cursus weet je waar een factuur is, welke stappen vanzelf gaan en '
    + 'welke op jou wachten, hoe je in tien seconden ziet of een bon klopt, en '
    + 'wat je doet als er iets niet klopt.',
  duurMinuten: 24,
  accent: 'blauw',
  slaagpercentage: 80,

  modules: [
    /* ================================================================ *
     *  1
     * ================================================================ */
    {
      id: 'f1',
      titel: 'De reis van een factuur',
      kop: 'Acht stations. Drie gaan vanzelf, vijf wachten op een mens.',
      blokken: [
        {
          soort: 'film',
          titel: 'Hij komt binnen',
          ondertitel: 'Waar een factuur begint.',
          scenes: [
            {
              shot: 'binnenkomst',
              duur: 6500,
              zeg:
                'Een leverancier mailt zijn factuur naar ons inkoopadres. Die '
                + 'belandt in het Postvak. Wat geen factuur is — een folder, een '
                + 'nieuwsbrief — komt er niet doorheen.',
            },
          ],
        },
        {
          soort: 'tekst',
          alineas: [
            'Een inkoopfactuur is een rekening die wíj moeten betalen. Hij komt bijna altijd per mail binnen, op een adres dat bij een vestiging hoort — zoiets als inkoop.venlo@. Vanaf daar loopt hij een vaste route tot hij betaald is.',
            'Hieronder staat die hele route. Let vooral op de rechterkolom: daar staat of de computer die stap doet of dat jij hem doet. Dat is de vraag die je straks het vaakst hebt — moet ik hier iets, of gaat dit vanzelf?',
          ],
        },
        {
          soort: 'stroom',
          titel: 'Van mail tot betaald',
          stappen: [
            {
              naam: 'De leverancier mailt',
              wat: 'Naar het inkoopadres van de vestiging. De mail komt in het Postvak.',
              wie: 'machine',
              icoon: 'mail',
            },
            {
              naam: 'De computer leest de bon',
              wat: 'Bedrag, btw, factuurnummer, leverancier, en aan welke bv hij gericht is.',
              wie: 'machine',
              icoon: 'ogen',
            },
            {
              naam: 'De computer deelt hem in',
              wat: 'Op welke grootboekrekening hij hoort. Uit het geheugen, of anders een gok.',
              wie: 'machine',
              icoon: 'brein',
            },
            {
              naam: 'Hij staat op Te valideren',
              wat: 'Bij Inkoop → Inkoopfacturen. Hier begint jouw werk.',
              wie: 'mens',
              icoon: 'vink',
            },
            {
              naam: 'Eerste akkoord',
              wat: 'Iemand kijkt de bon na en geeft het eerste akkoord.',
              wie: 'mens',
              icoon: 'vink',
            },
            {
              naam: 'Tweede handtekening',
              wat: 'Iemand anders tekent. Dezelfde persoon mag niet allebei.',
              wie: 'mens',
              icoon: 'twee',
            },
            {
              naam: 'Naar Exact',
              wat: 'Op het tabblad Naar Exact druk je op versturen. Ontbreekt er iets, dan blijft hij staan met de reden erbij.',
              wie: 'mens',
              icoon: 'kluis',
            },
            {
              naam: 'Betalen',
              wat: 'Je maakt een SEPA-bestand, laadt het bij de bank, en zet hem daarna op betaald.',
              wie: 'mens',
              icoon: 'bank',
            },
          ],
        },
        {
          soort: 'let-op',
          tekst:
            'Er is één afkorting: staat een leverancier al drie keer voor '
            + 'ongeveer hetzelfde bedrag door een mens goedgekeurd, dan mag de '
            + 'volgende vanzelf door. Dat gebeurt alleen als er niets aan te '
            + 'merken was. Meer daarover in module 4.',
        },
        {
          soort: 'kern',
          titel: 'Onthoud uit deze module',
          punten: [
            'Een factuur komt per mail binnen op het adres van een vestiging.',
            'De computer leest hem en deelt hem in; dat gaat vanzelf.',
            'Vanaf Te valideren wacht hij op mensen: eerst nakijken, dan tekenen.',
            'Naar Exact gaat alleen wat goedgekeurd én compleet is.',
          ],
        },
      ],
    },

    /* ================================================================ *
     *  2 -- uit de PowerPoint "uitleg systeem" van Casper (oktober 2026)
     *
     *  De schermafdrukken komen uit die presentatie. Twee ervan zijn hier NIET
     *  overgenomen: daarop stonden het IBAN, het KvK-nummer en het btw-nummer
     *  van een leverancier, plus de mailadressen van collega's. De academie is
     *  zichtbaar voor iedereen die hier werkt, en dat is niet de plek voor het
     *  rekeningnummer van een leverancier. Wat er wel in zit is bijgesneden tot
     *  het stuk waar de les in zit.
     * ================================================================ */
    {
      id: 'f1b',
      titel: 'De schermen, één voor één',
      kop: 'Nu je de route kent: waar staat het, en waar klik je.',
      blokken: [
        {
          soort: 'tekst',
          alineas: [
            'De route van de vorige module zie je terug in drie schermen: je startscherm, het scherm Inkoopfacturen, en het Postvak. Hieronder staan ze zoals ze er echt uitzien. Tik op het vergrootglas rechtsboven een plaat als je hem groter wilt zien.',
          ],
        },
        {
          soort: 'plaat',
          bron: 'startscherm',
          alt: 'Het startscherm van het dashboard na inloggen, met de kaart Op je bord en daaronder de systemen per categorie.',
          onderschrift: 'Je startscherm na inloggen. Dit is een oefenaccount, vandaar de nullen.',
          wijzers: [
            {
              naam: 'Het menu, linksboven',
              wat: 'De drie streepjes. Daar zit alles achter wat jij mag zien.',
            },
            {
              naam: 'Op je bord',
              wat: 'Wat op jóu wacht: inkoopfacturen, te verwerken post en je postvak. Begin hier.',
            },
            {
              naam: 'Systemen',
              wat: 'Alles per categorie, met een zoekveld. Hier vind je Inkoopfacturen, Postvak en de rest terug.',
            },
          ],
        },
        {
          soort: 'plaat',
          bron: 'tabbladen',
          alt: 'Het scherm Inkoopfacturen met bovenin de tellers en daaronder de tabbladen Te valideren, Wacht op tweede, Goedgekeurd en Naar Exact.',
          onderschrift: 'Inkoopfacturen. De tabbladen zijn de vier stappen, in volgorde.',
          wijzers: [
            {
              naam: 'Te valideren',
              wat: 'Alle nieuwe facturen en bonnen komen hier binnen. Hier kijk je na.',
            },
            {
              naam: 'Wacht op tweede',
              wat: 'Iemand heeft het eerste akkoord gegeven. Er moet nog een tweede handtekening bij.',
            },
            {
              naam: 'Goedgekeurd',
              wat: 'Twee handtekeningen gezet. Klaar om naar Exact te gaan.',
            },
            {
              naam: 'Naar Exact',
              wat: 'Staat klaar om verstuurd te worden. Hier druk je op versturen — en hier zie je wat blijft liggen.',
            },
          ],
        },
        {
          soort: 'let-op',
          tekst:
            'Het versturen naar Exact is nu nog een knop op dat laatste tabblad. '
            + 'Het staat op de rol om het vanzelf te laten gaan zodra de tweede '
            + 'handtekening er is — tot die tijd kijkt er iemand.',
        },
        {
          soort: 'tekst',
          alineas: [
            'Dan het Postvak. Een factuur die een leverancier rechtstreeks naar ons inkoopadres stuurt, wordt vanzelf een kostenpost. Een mail die jij dóórstuurt niet — en dat is met opzet. Zou elke doorgestuurde mail een factuur worden, dan werd ieder antwoord op een gesprek er ook een.',
          ],
        },
        {
          soort: 'plaat',
          bron: 'postvak',
          alt: 'Een geopende mail in het Postvak, met de bijlagen eronder en de knoppen Afgehandeld, Negeren, Terug op nieuw en Dit is een inkoopmail.',
          onderschrift: 'Een geopende mail in het Postvak. De knoppen onderin doen het werk.',
          wijzers: [
            {
              naam: 'Bijlagen opnieuw ophalen',
              wat: 'Zitten de bijlagen er nog niet bij? Druk hierop; daarna staan ze er.',
            },
            {
              naam: 'Dit is een inkoopmail',
              wat: 'Zit er een factuur bij? Hiermee maak je er een kostenpost van. Hij staat daarna bij Inkoopfacturen.',
            },
            {
              naam: 'Afgehandeld',
              wat: 'Geen factuur erbij? Hiermee is de mail klaar. Er wordt niets van gemaakt.',
            },
            {
              naam: 'Terug op nieuw',
              wat: 'Per ongeluk geopend? Hiermee staat hij weer als ongelezen voor de rest.',
            },
          ],
        },
        {
          soort: 'keuze',
          situatie:
            'Een collega stuurt je een mail door van een leverancier. Er zit een '
            + 'pdf bij met een factuur van € 212,-. Je opent hem in het Postvak.',
          vraag: 'Wat doe je?',
          opties: [
            {
              tekst: 'Niets — hij is doorgestuurd, dus het systeem maakt er vanzelf een kostenpost van.',
              goed: false,
              uitleg:
                'Juist niet. Bij een doorgestuurde mail gebeurt dat met opzet niet, '
                + 'anders werd elk antwoord op een gesprek een inkoopfactuur. Deze '
                + 'blijft liggen tot jij iets doet.',
            },
            {
              tekst: 'Bijlagen opnieuw ophalen, daarna Dit is een inkoopmail.',
              goed: true,
              uitleg:
                'Precies die twee knoppen, in die volgorde. De factuur staat daarna '
                + 'bij Inkoopfacturen op Te valideren.',
            },
            {
              tekst: 'Op Afgehandeld klikken en de factuur zelf doorsturen naar het inkoopadres.',
              goed: false,
              uitleg:
                'Dat kan, maar het is een omweg met een risico: stuur je hem twee '
                + 'keer, dan staat hij er twee keer. Gebruik de knop die ervoor is.',
            },
          ],
        },
        {
          soort: 'kern',
          titel: 'Onthoud uit deze module',
          punten: [
            'Op je bord staat wat op jou wacht. Daar begin je.',
            'De tabbladen bij Inkoopfacturen zijn de vier stappen: Te valideren, Wacht op tweede, Goedgekeurd, Naar Exact.',
            'Een rechtstreeks gemailde factuur wordt vanzelf een kostenpost; een doorgestuurde niet.',
            'Doorgestuurd met een factuur erbij? Bijlagen opnieuw ophalen, dan Dit is een inkoopmail.',
          ],
        },
      ],
    },

    /* ================================================================ *
     *  2
     * ================================================================ */
    {
      id: 'f2',
      titel: 'Wat de computer alvast doet',
      kop: 'Hij leest de bon en doet een voorstel. Een voorstel, geen besluit.',
      blokken: [
        {
          soort: 'film',
          titel: 'De computer leest hem',
          ondertitel: 'Wat er van een bon wordt afgehaald.',
          scenes: [
            {
              shot: 'lezen',
              duur: 6500,
              zeg:
                'De bon wordt uitgelezen: leverancier, bedrag, btw, factuurnummer. '
                + 'Waar hij niet zeker van is, zet hij een vraagteken — en dat '
                + 'vraagteken komt straks op jouw scherm te staan.',
            },
          ],
        },
        {
          soort: 'tekst',
          alineas: [
            'Het lezen gebeurt door een AI. Standaard op een pc hier op kantoor, zodat de bon het huis niet uit gaat; lukt dat niet, dan springt er een andere lezer bij. Wat eruit komt is altijd hetzelfde soort gegeven: wie stuurt de factuur, wat kost het, hoeveel btw, welk factuurnummer, en aan welke bv hij gericht is.',
            'Dat laatste leest hij van het stuk zelf — bij "aan" of "t.a.v." — en niet van het mailadres waarop hij binnenkwam. Een huurfactuur voor Vastgoed komt nu eenmaal binnen op het adres van de vestiging waar het pand staat.',
          ],
        },
        {
          soort: 'tekst',
          alineas: [
            'Daarna kiest de computer een grootboekrekening. Dat doet hij op twee manieren, en het verschil tussen die twee is het belangrijkste wat je uit deze module meeneemt.',
          ],
        },
        {
          soort: 'vergelijk',
          nietTitel: 'Een gok — kijk zelf',
          niet: [
            'Deze leverancier is hier nieuw.',
            'De rekening is geraden op woorden in de omschrijving.',
            'Op het scherm staat een merkje bij de rekening.',
            'Dit gaat nooit vanzelf door; er moet altijd iemand naar kijken.',
          ],
          welTitel: 'Uit het geheugen — waarschijnlijk goed',
          wel: [
            'Deze leverancier is eerder geboekt door een mens.',
            'Dezelfde rekening als de vorige keer.',
            'Kijk hem na, maar hij klopt meestal.',
            'Alleen dít kan na een paar keer vanzelf doorgaan.',
          ],
        },
        {
          soort: 'let-op',
          tekst:
            'De computer besluit niets. Hij doet een voorstel en zet erbij hoe '
            + 'zeker hij is. Wat hij invult en jij niet nakijkt, staat straks in '
            + 'de boekhouding — op jouw akkoord.',
        },
        {
          soort: 'kern',
          titel: 'Onthoud uit deze module',
          punten: [
            'De bon wordt gelezen; de bv komt van het stuk zelf, niet van het mailadres.',
            'De rekening komt uit het geheugen (meestal goed) of is een gok (zelf kijken).',
            'Waar de lezer twijfelde, staat dat erbij.',
          ],
        },
      ],
    },

    /* ================================================================ *
     *  3
     * ================================================================ */
    {
      id: 'f3',
      titel: 'Snel kijken of het klopt',
      kop: 'Acht regels, van boven naar beneden. In tien seconden weet je genoeg.',
      blokken: [
        {
          soort: 'tekst',
          alineas: [
            'Een kostenpost op je scherm is een lijstje regels. Je hoeft ze niet allemaal uit te rekenen — je leest ze van boven naar beneden en je let op drie dingen: staat er een merkje, klopt het bedrag met de bijlage, en komt deze factuur me bekend voor.',
            'Hieronder staat een echte kostenpost zoals je hem krijgt. Er zitten twee dingen in die niet kloppen. Tik aan wat je niet vertrouwt en kijk hem daarna na.',
          ],
        },
        {
          soort: 'kern',
          titel: 'Drie onderdelen, altijd in deze volgorde',
          punten: [
            'Voorgelezen door AI — lees eerst waar de computer niet uit kwam, zoals een misgelezen IBAN of KvK-nummer.',
            'Relatie in Exact — staat de juiste leverancier eraan? Pas hier ook factuurnummer en vervaldatum aan als die niet kloppen.',
            'Boeking — klopt de grootboekrekening, de onderneming en het rekeningnummer?',
          ],
        },
        {
          soort: 'let-op',
          tekst:
            'Klopt alles? Rechtsboven staat Goedkeuren. Klopt er iets niet, dan '
            + 'staan daar ook Afkeuren en Opnieuw lezen.',
        },
        {
          soort: 'kijk',
          inleiding:
            'Tik de regels aan die je niet vertrouwt. Je kunt er niets mee '
            + 'stukmaken, en te veel aantikken kost je niets.',
          regels: [
            {
              id: 'k1',
              label: 'Twijfel van de lezer',
              waarde: '1 punt',
              merk: { tekst: 'onderneming niet zeker', soort: 'let-op' },
              controle:
                'Dit lees je als eerste. De lezer zegt hier zelf waar hij niet '
                + 'uit kwam, en hij wijst meestal precies de regel aan die je '
                + 'moet nakijken. Staat hier niets, dan ging het lezen vlot.',
            },
            {
              id: 'k2',
              label: 'Leverancier',
              waarde: 'Shell Nederland Verkoopmij B.V.',
              merk: { tekst: 'gekoppeld', soort: 'goed' },
              controle:
                'Staat er een leverancier, en is hij gekoppeld aan een crediteur '
                + 'in Exact? Zonder koppeling gaat hij later niet door naar Exact '
                + 'en blijft hij hangen zonder dat iemand het merkt.',
            },
            {
              id: 'k3',
              label: 'Factuurnummer',
              waarde: '2026-04417',
              controle:
                'Komt dit nummer je bekend voor? Dezelfde factuur twee keer is de '
                + 'meest voorkomende fout, en de duurste. Het systeem waarschuwt '
                + 'bij een nummer dat al bestaat.',
            },
            {
              id: 'k4',
              label: 'Datum / vervalt',
              waarde: '14-09-2026 / 14-10-2026',
              controle:
                'Is de factuurdatum van een periode die nog open staat, en is de '
                + 'vervaldatum nog niet voorbij? Een bon die al over de vervaldatum '
                + 'is, hoort voorrang te krijgen.',
            },
            {
              id: 'k5',
              label: 'Bedrag (incl. btw)',
              waarde: '€ 1.247,50',
              controle:
                'Staat dit ook echt zo op de bijlage? Open hem even. Dit is het '
                + 'bedrag dat straks de deur uit gaat.',
            },
            {
              id: 'k6',
              label: 'Btw (21%)',
              waarde: '€ 124,75',
              merk: { tekst: 'uit de bon gelezen', soort: 'neutraal' },
              mis:
                'Bij 21% hoort hier ongeveer € 216 te staan, niet € 124,75 — dat '
                + 'is precies 10%. De lezer heeft het verkeerde getal van de bon '
                + 'gepakt. Zet het goed voordat je tekent; anders klopt de '
                + 'btw-aangifte niet.',
            },
            {
              id: 'k7',
              label: 'Onderneming',
              waarde: 'Truckwash 1 Venlo B.V.',
              controle:
                'Hier wees de twijfel naar. Kijk op de bijlage bij "aan" of '
                + '"t.a.v." aan wie hij gericht is — dát is de bv waar hij op '
                + 'hoort, ook als hij op een ander mailadres binnenkwam. In dit '
                + 'geval klopte het. Twijfel is een reden om te kijken, geen '
                + 'bewijs dat het fout is.',
            },
            {
              id: 'k8',
              label: 'Grootboekrekening',
              waarde: '4310 Brandstof',
              merk: { tekst: 'gok', soort: 'let-op' },
              mis:
                'Het merkje zegt "gok": deze leverancier is hier nieuw en de '
                + 'rekening is geraden op woorden in de omschrijving. Kijk zelf '
                + 'of 4310 de juiste is. Wat jij nu kiest, onthoudt het systeem '
                + 'voor de volgende keer — dus een fout hier gaat vanzelf door.',
            },
          ],
          naderhand:
            'Twee dingen zaten fout: de btw was 10% in plaats van 21%, en de '
            + 'rekening was een gok. Allebei herken je aan het merkje of aan één '
            + 'keer hoofdrekenen — je hoeft de hele bon niet door te spitten.',
        },
        {
          soort: 'kern',
          titel: 'Het rijtje, van boven naar beneden',
          punten: [
            'Staat er twijfel? Lees die eerst; hij wijst de regel aan.',
            'Leverancier gekoppeld? Zo niet, blijft hij later steken.',
            'Factuurnummer bekend? Dubbel is de duurste fout.',
            'Bedrag gelijk aan de bijlage? Even openklikken.',
            'Btw ongeveer een vijfde van het bedrag? Zo niet: nakijken.',
            'Rekening uit het geheugen of een gok? Bij een gok zelf kiezen.',
          ],
        },
      ],
    },

    /* ================================================================ *
     *  4
     * ================================================================ */
    {
      id: 'f4',
      titel: 'Twee handtekeningen',
      kop: 'Eén die kijkt, één die tekent. En dat mag niet dezelfde zijn.',
      blokken: [
        {
          soort: 'film',
          titel: 'Vier ogen',
          ondertitel: 'Waarom er twee mensen aan te pas komen.',
          scenes: [
            {
              shot: 'vierogen',
              duur: 6500,
              zeg:
                'De eerste kijkt na en geeft akkoord. Een ander zet de tweede '
                + 'handtekening. Probeer je ze allebei zelf te zetten, dan gaat de '
                + 'stempel niet omlaag — ook niet als je in beide groepen staat.',
            },
          ],
        },
        {
          soort: 'tekst',
          alineas: [
            'Hier gaat geld weg, en één iemand die zich vergist is genoeg als er maar één handtekening nodig is. Daarom zijn er twee stappen. Een factuur loopt van open naar eerste akkoord naar goedgekeurd. Wie de eerste zette, mag de tweede niet zetten — dat wordt niet door het scherm bewaakt maar door de database zelf, dus er is geen omweg.',
            'Per bv staat ingesteld wie de eerste mag geven en wie mag tekenen. Dat zijn groepen en geen personen: gaat er iemand op vakantie, dan ligt de stapel niet stil. Eén uit de groep is genoeg.',
            'Afkeuren kan altijd, in elke stand, door één persoon. Dat is geen inconsistentie: tegenhouden kan geen kwaad, doorlaten wel.',
          ],
        },
        {
          soort: 'keuze',
          situatie:
            'Je hebt vanochtend het eerste akkoord gegeven op een factuur van de '
            + 'groothandel. Het is vrijdagmiddag, je collega is weg, en de factuur '
            + 'vervalt maandag.',
          vraag: 'Wat doe je?',
          opties: [
            {
              tekst: 'Zelf ook de tweede handtekening zetten; het is tenslotte nagekeken.',
              goed: false,
              uitleg:
                'Dat lukt niet — de database houdt het tegen. En dat is maar goed '
                + 'ook: dan was vier ogen een gewoonte geweest in plaats van een '
                + 'regel.',
            },
            {
              tekst: 'Iemand anders uit de groep vragen om te tekenen.',
              goed: true,
              uitleg:
                'Precies. De tweede stap is een groep en geen persoon, juist voor '
                + 'dit soort momenten. Eén uit de groep is genoeg.',
            },
            {
              tekst: 'De factuur afkeuren en maandag opnieuw indienen.',
              goed: false,
              uitleg:
                'Dat kan wel, maar je gooit er werk mee weg en de leverancier '
                + 'wacht nog steeds. Afkeuren is voor een factuur die niet deugt, '
                + 'niet voor een agenda die niet uitkomt.',
            },
          ],
        },
        {
          soort: 'tekst',
          alineas: [
            'Er is één uitzondering op de twee handtekeningen: een factuur kan vanzelf goedgekeurd worden. Dat gebeurt alleen als álle vier de voorwaarden kloppen — dezelfde leverancier is al een paar keer door een mens goedgekeurd voor ongeveer hetzelfde bedrag, de lezer twijfelde nergens over, de rekening komt uit het geheugen, en het is een gewone factuur of bon. Een factuurnummer dat al bestaat gaat nooit vanzelf door, en een aanmaning ook niet.',
          ],
        },
        {
          soort: 'keuze',
          situatie:
            'Je ziet in de lijst een factuur met de stand "Vanzelf goedgekeurd". '
            + 'Je hebt er zelf niet naar gekeken.',
          vraag: 'Wat betekent dat?',
          opties: [
            {
              tekst: 'Er is iets misgegaan; iemand heeft de controle overgeslagen.',
              goed: false,
              uitleg:
                'Nee. Dit is een ingebouwde route die alleen opengaat als vier '
                + 'voorwaarden tegelijk kloppen. Hij staat erbij zodat je kunt '
                + 'zien dat het zo gegaan is.',
            },
            {
              tekst: 'Deze leverancier stuurt al langer hetzelfde, er was geen twijfel, en de rekening kwam uit het geheugen.',
              goed: true,
              uitleg:
                'Klopt. En je kunt hem nog steeds openen en terugzetten als er '
                + 'iets niet klopt — vanzelf goedgekeurd is geen gesloten deur.',
            },
            {
              tekst: 'Alle facturen onder een bepaald bedrag gaan vanzelf door.',
              goed: false,
              uitleg:
                'Er is wel een plafond, maar een laag bedrag alleen is niet '
                + 'genoeg. Een nieuwe leverancier van drie euro gaat net zo goed '
                + 'langs twee mensen.',
            },
          ],
        },
        {
          soort: 'kern',
          titel: 'Onthoud uit deze module',
          punten: [
            'Open → eerste akkoord → goedgekeurd. Twee verschillende mensen.',
            'Wie de eerste zette, kan de tweede niet zetten. De database houdt dat tegen.',
            'Per bv staat een groep ingesteld, geen persoon. Eén uit de groep is genoeg.',
            'Afkeuren mag altijd, door één persoon.',
            'Vanzelf goedgekeurd kan, maar alleen bij een bekende leverancier zonder twijfel.',
          ],
        },
      ],
    },

    /* ================================================================ *
     *  5
     * ================================================================ */
    {
      id: 'f5',
      titel: 'Naar Exact, en dan betalen',
      kop: 'Goedgekeurd is niet hetzelfde als weg. Er moet nog iets kloppen.',
      blokken: [
        {
          soort: 'film',
          titel: 'Door het hek',
          ondertitel: 'Wat er na het goedkeuren gebeurt.',
          scenes: [
            {
              shot: 'naar-exact',
              duur: 6500,
              zeg:
                'Een goedgekeurde factuur gaat naar Exact. De volgende komt niet '
                + 'door: er is geen crediteur aan gekoppeld. Hij blijft staan, met '
                + 'de reden erbij.',
            },
          ],
        },
        {
          soort: 'tekst',
          alineas: [
            'Goedkeuren is niet het einde. Een goedgekeurde factuur komt op het tabblad Naar Exact te staan, en daar druk je op versturen. Daarvoor moeten vier dingen bekend zijn: het inkoopdagboek, de leverancier als crediteur in Exact, de btw-code, en de grootboekrekening. Ontbreekt er één, dan blijft de factuur staan — niet stil en niet onzichtbaar: op het scherm staat wat er ontbreekt en wat je eraan doet.',
            'Dat versturen is nu nog een knop. Het staat op de rol om het automatisch te laten gaan zodra de tweede handtekening er is; tot die tijd hoort er iemand op dat tabblad te kijken, want wat daar blijft staan, staat nergens anders.',
            'Het ontbrekende stuk is bijna altijd de crediteur. Dezelfde leverancier staat in Exact ruim twintig keer, één keer per bv, dus de computer kan er niet zelf één uitkiezen. Dat koppel je één keer met de hand bij Inkoop → Leveranciers, en daarna is het voor altijd geregeld.',
          ],
        },
        {
          soort: 'vergelijk',
          nietTitel: 'Blijft liggen tot jij iets doet',
          niet: [
            'De leverancier is nog niet aan een crediteur in Exact gekoppeld.',
            'De grootboekrekening ontbreekt of bestaat niet in die bv.',
            'De btw-code of het dagboek staat voor die bv niet ingesteld.',
            'Een gesplitste factuur waarvan de regels niet optellen tot het bedrag.',
          ],
          welTitel: 'Gaat vanzelf',
          wel: [
            'Een goedgekeurde factuur waar niets aan ontbreekt gaat naar Exact.',
            'Het factuurnummer en de bijlage gaan mee.',
            'Wat is doorgekomen staat terug te lezen in het overzicht.',
            'Mislukt het bij Exact, dan staat de foutmelding erbij.',
          ],
        },
        {
          soort: 'tekst',
          alineas: [
            'De laatste stap is betalen, en die is met opzet handwerk. Je maakt bij Financieel → Betalen een SEPA-bestand met de openstaande facturen erin, laadt dat bij de bank, en zet de facturen daarna op betaald. Een bestand maken is niet hetzelfde als geld overmaken — de bank kan het weigeren, iemand kan vergeten te fiatteren. Daarom zet het systeem ze niet zelf op betaald.',
          ],
        },
        {
          soort: 'kern',
          titel: 'Onthoud uit deze module',
          punten: [
            'Naar Exact gaat alleen wat goedgekeurd is én compleet — en iemand drukt er nu nog op.',
            'Het ontbrekende stuk is bijna altijd de crediteur; die koppel je bij Leveranciers.',
            'Wat blijft liggen staat op het scherm, met de reden erbij.',
            'Betalen doe je zelf: SEPA-bestand maken, naar de bank, dan op betaald zetten.',
          ],
        },
      ],
    },

    /* ================================================================ *
     *  6
     * ================================================================ */
    {
      id: 'f6',
      titel: 'Als er iets niet klopt',
      kop: 'De tien gevallen die echt voorkomen, en wat je ermee doet.',
      blokken: [
        {
          soort: 'tekst',
          alineas: [
            'De meeste facturen lopen zonder gedoe door. Wat hieronder staat is de rest — en bij de helft daarvan kun je zelf iets, terwijl mensen vaak denken van niet. Tik per geval aan wat je doet.',
          ],
        },
        {
          soort: 'sorteer',
          vraag: 'Wat doe je hiermee?',
          linksLabel: 'Tegenhouden',
          rechtsLabel: 'Zelf oplossen',
          kaarten: [
            {
              id: 'u1',
              tekst: 'Bij de bon staat een rood bolletje en "lezen mislukt".',
              rechts: true,
              uitleg:
                'Druk op "Opnieuw lezen". Lezen mislukt bijna altijd door iets '
                + 'tijdelijks: de pc stond uit, het model was aan het laden, het '
                + 'netwerk hikte. Morgen is dat meestal over.',
            },
            {
              id: 'u2',
              tekst: 'De bon staat al een half uur op "bezig" en er beweegt niets.',
              rechts: true,
              uitleg:
                'Geef hem vrij; dan gaat hij terug in de rij. De pc is er '
                + 'halverwege mee opgehouden, en dan hoort die bon niet voor '
                + 'eeuwig geclaimd te blijven.',
            },
            {
              id: 'u3',
              tekst: 'Het is een verkoopfactuur van onszelf, die tussen de kosten is beland.',
              rechts: false,
              uitleg:
                'Niet goedkeuren. Een factuur die wij hebben verstuurd is geen '
                + 'kostenpost; die hoort bij Verkoopfacturen. Keur hem af of gooi '
                + 'hem weg, met de reden erbij.',
            },
            {
              id: 'u4',
              tekst: 'De factuur is gericht aan Truckwash 1 Vastgoed, maar kwam binnen op het adres van Venlo.',
              rechts: true,
              uitleg:
                'Zet de onderneming goed en ga door. Wat op het stuk staat bij '
                + '"aan" telt, niet het mailadres waarop hij binnenkwam. Een '
                + 'huurfactuur komt nu eenmaal binnen waar het pand staat.',
            },
            {
              id: 'u5',
              tekst: 'Hetzelfde factuurnummer staat er al een keer in.',
              rechts: false,
              uitleg:
                'Tegenhouden. Dit is de duurste fout die er is, want dan betaal '
                + 'je twee keer. Kijk eerst welke van de twee de echte is voordat '
                + 'je er een weggooit.',
            },
            {
              id: 'u6',
              tekst: 'De factuur is voor drie vestigingen tegelijk.',
              rechts: true,
              uitleg:
                'Splitsen. Je maakt regels met per vestiging het bedrag en de '
                + 'rekening. Let op: de regels moeten samen precies op het bedrag '
                + 'van de bon uitkomen, anders laat hij zich niet goedkeuren.',
            },
            {
              id: 'u7',
              tekst: 'De leverancier is nog niet gekoppeld aan een crediteur in Exact.',
              rechts: true,
              uitleg:
                'Koppelen bij Inkoop → Leveranciers. Eén keer met de hand, daarna '
                + 'voor altijd. Zonder dit keur je hem wel goed maar komt hij nooit '
                + 'in Exact aan.',
            },
            {
              id: 'u8',
              tekst: 'Het is een aanmaning van een factuur die al betaald is.',
              rechts: false,
              uitleg:
                'Niet goedkeuren. Zoek eerst de oorspronkelijke factuur op. Een '
                + 'aanmaning gaat trouwens nooit vanzelf door — dat is met opzet, '
                + 'want een aanmaning is per definitie een tweede keer.',
            },
            {
              id: 'u9',
              tekst: 'Het bedrag is vier keer zo hoog als wat deze leverancier normaal stuurt.',
              rechts: false,
              uitleg:
                'Eerst navragen. Het kan kloppen — een jaarafrekening, een grote '
                + 'levering — maar dat weet je pas als je het hebt gevraagd. Zet '
                + 'je vraag in de notitie, dan ziet de tweede ondertekenaar hem ook.',
            },
            {
              id: 'u10',
              tekst: 'Je gaf vanochtend het eerste akkoord en wil hem nu ook tekenen.',
              rechts: false,
              uitleg:
                'Dat gaat niet, en terecht. Vraag iemand anders uit de groep. Dit '
                + 'is precies waar vier ogen voor is.',
            },
          ],
        },
        {
          soort: 'let-op',
          tekst:
            'Weet je het niet? Keur hem dan niet goed. Een factuur die blijft '
            + 'liggen kost een telefoontje; een factuur die ten onrechte is '
            + 'goedgekeurd kost geld en staat bovendien in de boekhouding.',
        },
        {
          soort: 'tekst',
          alineas: [
            'En als het systeem zelf niet doet wat het hoort te doen: meld het. Dat kan op drie plekken, elk voor iets anders.',
          ],
        },
        {
          soort: 'vergelijk',
          nietTitel: 'Eerst zelf proberen',
          niet: [
            'Cowork — stel een gerichte vraag; die zoekt het antwoord in onze eigen systemen.',
            'Academy — hier, om een onderwerp rustig door te nemen.',
            'Dringend? Bel. Maak daarna alsnog een melding voor de achtergrond.',
          ],
          welTitel: 'Melden aan de ontwikkelaar',
          wel: [
            'De drie puntjes naast je initialen, rechtsboven.',
            'Gebruik dit bij fouten, bugs en suggesties.',
            'Er gaat vanzelf bij: welke versie je gebruikt, welke fouten op de achtergrond speelden, en waar je hebt geklikt.',
            'Daarmee is de situatie meteen te zien, en dat scheelt heen-en-weer.',
          ],
        },
        {
          soort: 'kern',
          titel: 'Onthoud uit deze module',
          punten: [
            'Lezen mislukt of vastgelopen? Opnieuw lezen of vrijgeven — dat mag je zelf.',
            'Verkeerde bv, splitsing nodig, crediteur ontbreekt? Zelf oplossen.',
            'Dubbel nummer, aanmaning, verkoopfactuur, raar bedrag? Tegenhouden.',
            'Bij twijfel: niet goedkeuren, wel een notitie erbij zetten.',
            'Werkt het systeem niet? Melden via de drie puntjes naast je initialen.',
          ],
        },
      ],
    },
  ],

  /* ================================================================== *
   *  De toets
   * ================================================================== */
  toets: [
    {
      id: 'ft1',
      vraag: 'Welke stappen in de route wachten op een mens?',
      opties: [
        'Alleen het goedkeuren',
        'Nakijken, eerste akkoord, tweede handtekening, versturen naar Exact en betalen',
        'Alleen het betalen',
        'Alle stappen; de computer doet niets zelf',
      ],
      goed: 1,
      uitleg:
        'Binnenkomen, lezen en indelen gaan vanzelf. De rest wacht op iemand — '
        + 'ook het versturen naar Exact, want dat is nu nog een knop.',
    },
    {
      id: 'ft2',
      situatie:
        'Bij de grootboekrekening op een kostenpost staat een merkje met "gok".',
      vraag: 'Wat betekent dat?',
      opties: [
        'Het systeem weet het zeker, maar wil het bevestigd hebben',
        'Deze leverancier is hier nieuw en de rekening is geraden op woorden',
        'De rekening bestaat niet in Exact',
        'De factuur is al een keer afgekeurd',
      ],
      goed: 1,
      uitleg:
        'Uit het geheugen betekent: zo is deze leverancier eerder door een mens '
        + 'geboekt. Een gok betekent dat dat geheugen er nog niet is. Kijk zelf.',
    },
    {
      id: 'ft3',
      situatie:
        'Op een bon staat een bedrag van € 1.000 inclusief btw, en de btw is '
        + 'gelezen als € 100.',
      vraag: 'Wat valt je op?',
      opties: [
        'Niets, dat kan kloppen',
        'Bij 21% zou er ongeveer € 173 moeten staan; dit lijkt 10%',
        'De btw hoort altijd precies 21% van het totaal te zijn',
        'Het bedrag is te laag voor btw',
      ],
      goed: 1,
      uitleg:
        'Even hoofdrekenen: de btw is grofweg een vijfde van het bedrag '
        + 'inclusief. Wijkt dat ver af, dan heeft de lezer het verkeerde getal '
        + 'van de bon gepakt.',
    },
    {
      id: 'ft4',
      vraag: 'Je gaf het eerste akkoord. Mag je ook de tweede handtekening zetten?',
      opties: [
        'Ja, als je in beide groepen staat',
        'Ja, bij een bedrag onder de drempel',
        'Nee; dat wordt in de database tegengehouden',
        'Nee, behalve als je leidinggevende is',
      ],
      goed: 2,
      uitleg:
        'Het scherm kan de knop verbergen, maar de regel staat in de database '
        + 'zelf. Daar geldt hij altijd, ook via een omweg.',
    },
    {
      id: 'ft5',
      situatie:
        'Een goedgekeurde factuur staat er al dagen en is nog niet in Exact.',
      vraag: 'Wat is de meest waarschijnlijke reden?',
      opties: [
        'Exact is traag',
        'De leverancier is nog niet aan een crediteur in Exact gekoppeld',
        'Het bedrag is te hoog',
        'Er is nog maar één handtekening',
      ],
      goed: 1,
      uitleg:
        'Dezelfde leverancier staat in Exact per bv apart, dus de computer kan '
        + 'er niet zelf één kiezen. Koppel hem bij Inkoop → Leveranciers; op het '
        + 'scherm staat welke reden het is.',
    },
    {
      id: 'ft6',
      situatie:
        'Een factuur is voor drie vestigingen tegelijk en je splitst hem in drie regels.',
      vraag: 'Waar moet je op letten?',
      opties: [
        'Dat elke regel een eigen factuurnummer krijgt',
        'Dat de regels samen precies op het bedrag van de bon uitkomen',
        'Dat je hem daarna drie keer goedkeurt',
        'Dat alle regels op dezelfde grootboekrekening staan',
      ],
      goed: 1,
      uitleg:
        'Het bedrag op de bon is leidend — dat is wat de leverancier vraagt. '
        + 'Kloppen de regels daar niet mee, dan laat hij zich niet goedkeuren.',
    },
    {
      id: 'ft7',
      vraag: 'Wanneer wordt een factuur vanzelf goedgekeurd?',
      opties: [
        'Als het bedrag onder de drempel ligt',
        'Als de leverancier al een paar keer door een mens is goedgekeurd voor ongeveer hetzelfde bedrag, er geen twijfel was en de rekening uit het geheugen komt',
        'Altijd bij een bekende leverancier',
        'Nooit; alles gaat langs twee mensen',
      ],
      goed: 1,
      uitleg:
        'Vier voorwaarden tegelijk, en een factuurnummer dat al bestaat gaat er '
        + 'nooit doorheen. Een aanmaning ook niet.',
    },
    {
      id: 'ft8',
      situatie:
        'Er komt een factuur binnen op inkoop.venlo@, maar op het stuk staat '
        + 'bij "aan": Truckwash 1 Vastgoed B.V.',
      vraag: 'Op welke bv hoort hij?',
      opties: [
        'Venlo, want daar kwam hij binnen',
        'Vastgoed, want dat staat op de factuur zelf',
        'Dat maakt niet uit; het wordt later rechtgezet',
        'Allebei, via een splitsing',
      ],
      goed: 1,
      uitleg:
        'Wat op het stuk staat telt. De leverancier heeft het erop gezet omdat '
        + 'hij van ons te horen kreeg naar wie hij moest factureren. Een post op '
        + 'de verkeerde bv staat in de jaarrekening van de verkeerde vennootschap.',
    },
  ],
}
